import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui-kit/form';
import type { CustomFieldDefinition } from '../../types/custom-field-definitions.types';

type CustomFieldInputProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  definition: CustomFieldDefinition & { Key: string; Label: string; DataType: string };
  name: FieldPath<TFieldValues>;
};

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
] as const;

const FIELD_LABEL_CLASS = 'text-[13px] font-medium text-neutral-900';
const FIELD_CONTROL_CLASS = 'h-10 border-neutral-300';

const AttributeFormLabel = ({
  definition,
}: {
  definition: CustomFieldInputProps<FieldValues>['definition'];
}) => (
  <FormLabel className={FIELD_LABEL_CLASS}>
    {definition.Label}
    {definition.Required ? <span className="text-destructive"> *</span> : null}
  </FormLabel>
);

export const CustomFieldInput = <TFieldValues extends FieldValues>({
  control,
  definition,
  name,
}: CustomFieldInputProps<TFieldValues>) => {
  const dataType = definition.DataType;
  const options = (definition.Options ?? []).filter((option): option is string =>
    Boolean(option?.trim())
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        if (dataType === 'Boolean') {
          return (
            <FormItem>
              <AttributeFormLabel definition={definition} />
              <FormControl>
                <RadioGroup
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                  className="flex flex-row gap-4"
                >
                  {BOOLEAN_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      htmlFor={`${definition.Key}-${option.value}`}
                      className="flex cursor-pointer items-center gap-2 text-[13px] font-normal text-neutral-900"
                    >
                      <RadioGroupItem
                        value={option.value}
                        id={`${definition.Key}-${option.value}`}
                        className="border-neutral-400 text-primary-600"
                      />
                      {option.label}
                    </label>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage data-custom-field-error="true" />
            </FormItem>
          );
        }

        if (dataType === 'Dropdown') {
          return (
            <FormItem>
              <AttributeFormLabel definition={definition} />
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className={FIELD_CONTROL_CLASS}>
                    <SelectValue placeholder={`Select ${definition.Label.toLowerCase()}`} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {field.value && !options.includes(field.value) ? (
                    <SelectItem value={field.value}>{field.value}</SelectItem>
                  ) : null}
                  {options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage data-custom-field-error="true" />
            </FormItem>
          );
        }

        if (dataType === 'MultiSelect' && options.length > 0) {
          const selected = field.value
            ? field.value
                .split(',')
                .map((item: string) => item.trim())
                .filter(Boolean)
            : [];

          const toggleOption = (option: string, checked: boolean) => {
            const next = checked
              ? [...new Set([...selected, option])]
              : selected.filter((item: string) => item !== option);
            field.onChange(next.join(', '));
          };

          return (
            <FormItem>
              <AttributeFormLabel definition={definition} />
              <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border border-neutral-200 p-3">
                {options.map((option) => (
                  <div key={option} className="flex items-center gap-2">
                    <Checkbox
                      id={`${definition.Key}-${option}`}
                      checked={selected.includes(option)}
                      onCheckedChange={(checked) => toggleOption(option, checked === true)}
                    />
                    <Label
                      htmlFor={`${definition.Key}-${option}`}
                      className="text-[13px] font-normal text-neutral-900"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </div>
              <FormMessage data-custom-field-error="true" />
            </FormItem>
          );
        }

        if (dataType === 'Date') {
          return (
            <FormItem>
              <AttributeFormLabel definition={definition} />
              <FormControl>
                <Input
                  type="date"
                  className={FIELD_CONTROL_CLASS}
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage data-custom-field-error="true" />
            </FormItem>
          );
        }

        if (dataType === 'Number' || dataType === 'NumberRange') {
          return (
            <FormItem>
              <AttributeFormLabel definition={definition} />
              <FormControl>
                <Input
                  type="number"
                  placeholder="0"
                  className={FIELD_CONTROL_CLASS}
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage data-custom-field-error="true" />
            </FormItem>
          );
        }

        if (dataType === 'MultiSelect') {
          return (
            <FormItem>
              <AttributeFormLabel definition={definition} />
              <FormControl>
                <Input
                  placeholder="Enter values separated by commas"
                  className={FIELD_CONTROL_CLASS}
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage data-custom-field-error="true" />
            </FormItem>
          );
        }

        return (
          <FormItem>
            <AttributeFormLabel definition={definition} />
            <FormControl>
              <Input
                placeholder={definition.Label}
                className={FIELD_CONTROL_CLASS}
                {...field}
                value={field.value ?? ''}
              />
            </FormControl>
            <FormMessage data-custom-field-error="true" />
          </FormItem>
        );
      }}
    />
  );
};
